import type { PregledKazalnikov, Porocilo, VrsticaPorocila } from '@koda/shared';
import type { JSX } from 'react';

import { znesek } from '../lib/format';
import { useBesedila } from '../lib/jezik';
import { GrafDenarja, GrafDobicka, GrafMarze } from './GrafiPodrobno';
import { GrafPrihodkov, STOLPCI_SIROKO } from './GrafPrihodkov';
import { celicaDelov, obdobjeLeta } from './kazalniki-skupno';
import type { Celica } from './PanelRazclenitve';
import { TabelaPrimerjave } from './TabelaPrimerjave';

interface Lastnosti {
  tabele: Porocilo[];
  pregled: PregledKazalnikov;
  naCelico: (celica: Celica) => void;
}

/** Tabela, ki ima vrstico s tem ključem (bloki imajo stalne ključe vrstic). */
const zVrstico = (tabele: Porocilo[], kljuc: string): Porocilo | undefined =>
  tabele.find((t) => t.vrstice.some((v) => v.kljuc === kljuc));
const vrstica = (t: Porocilo | undefined, kljuc: string): VrsticaPorocila | undefined => t?.vrstice.find((v) => v.kljuc === kljuc);

/**
 * Zavihek Podrobno (zasnova »Podrobno – splet«, docs/ARHITEKTURA.md, 6.3), od
 * vrha: Prihodki po mesecih (~2/3) in Celotni dobiček po mesecih (~1/3);
 * Rezultat po mesecih; Denarni tok z grafom stanja denarja (~3/5) in Razlika
 * v ceni z grafom profitne marže (~2/5); Primerjava do meseca n s puščicami;
 * opomba. Ploščice so nad stranjo (skupne s Pregledom). Številke so iste kot
 * v tabelah in izvozu; vsaka, ki je vsota knjižb, odpre razčlenitev.
 */
export function PodrobnoKazalnikov({ tabele, pregled, naCelico }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const primerjalno = pregled.imaPrimerjalno ? pregled.primerjalno : null;
  const obdobje = obdobjeLeta(pregled.n, pregled.leto, b.meseci);
  const ploscica = (koda: string) => pregled.ploscice.find((p) => p.koda === koda && p.poMesecih.t.length > 0);
  const prihodki = ploscica('prihodki');
  const dobicek = ploscica('celotni_dobicek');
  const denar = ploscica('denar');

  const rezultat = zVrstico(tabele, 'celotni-dobicek');
  const razlika = zVrstico(tabele, 'profitna-marza');
  const denarniTok = zVrstico(tabele, 'cf-izid');
  const doMeseca = zVrstico(tabele, 'prihodki-do');
  const ostale = tabele.filter((t) => ![rezultat, razlika, denarniTok, doMeseca].includes(t));

  // Prihodki primerjalnega leta so v grafu prihodkov (in v izvozu), stanje
  // denarja v grafu denarja – v tabelah pod njima se ne ponavljajo.
  const brez = (t: Porocilo, kljuci: string[]): Porocilo => ({ ...t, vrstice: t.vrstice.filter((v) => !kljuci.includes(v.kljuc)) });

  const marza = vrstica(razlika, 'profitna-marza');
  const znesekRazlike = (kljuc: string): JSX.Element | null => {
    const v = vrstica(razlika, kljuc);
    if (v === undefined) return null;
    const deli = v.deliSkupaj ?? [];
    return (
      <div className="kz-podatek" key={kljuc}>
        <span className="kz-podatek-naziv">{v.naziv}</span>
        {v.skupaj === '' ? (
          <span className="kz-podatek-vrednost">{b.pk.ni}</span>
        ) : deli.length === 0 ? (
          <span className="kz-podatek-vrednost">{znesek(v.skupaj)}</span>
        ) : (
          <button
            type="button"
            className="kz-podatek-vrednost kz-tiho-gumb"
            onClick={() => {
              naCelico(celicaDelov(v.naziv, obdobje, v.skupaj, deli));
            }}
          >
            {znesek(v.skupaj)}
          </button>
        )}
      </div>
    );
  };
  const konti = (kljuc: string): string =>
    [...new Set((vrstica(razlika, kljuc)?.deliSkupaj ?? []).flatMap((d) => d.filter.predpone ?? []))].join(', ') || b.pk.ni;

  const kartaDenarja =
    denarniTok === undefined ? null : (
      <section className="kz-karta" aria-label={denarniTok.naslov}>
        <div className="kz-karta-naslov">
          <h2>{denarniTok.naslov}</h2>
          <div className="kz-karta-podnaslov">{b.pk.denarniTokPodnaslov}</div>
        </div>
        {denar !== undefined && <GrafDenarja ploscica={denar} leto={pregled.leto} naCelico={naCelico} />}
        <TabelaPrimerjave
          tabele={[denar === undefined ? denarniTok : brez(denarniTok, ['cf-zacetno', 'cf-koncno'])]}
          naCelico={naCelico}
          naslovVrstic={b.kazalnik}
          brezNaslova
        />
      </section>
    );
  const kartaRazlike =
    razlika === undefined || marza === undefined ? null : (
      <section className="kz-karta" aria-label={razlika.naslov}>
        <div className="kz-karta-naslov">
          <h2>{razlika.naslov}</h2>
          <div className="kz-karta-podnaslov">{b.pk.razlikaVCeniPodnaslov}</div>
        </div>
        <GrafMarze marza={marza} stolpci={razlika.stolpci} />
        <div className="kz-podatki-razlike">{['prodajna', 'nabavna', 'razlika-v-ceni'].map(znesekRazlike)}</div>
        <p className="kz-karta-podnaslov kz-konti">{b.pk.kontiRazlike(konti('nabavna'), konti('prodajna'))}</p>
        <details className="kz-po-mesecih">
          <summary>{b.pk.poMesecih}</summary>
          <TabelaPrimerjave tabele={[razlika]} naCelico={naCelico} naslovVrstic={b.kazalnik} brezNaslova />
        </details>
      </section>
    );

  const dan = new Date(Date.UTC(pregled.leto, pregled.n, 0)).getUTCDate();

  return (
    <div className="podrobno-kazalnikov">
      {(prihodki !== undefined || dobicek !== undefined) && (
        <div className={`kz-vrsta-2-1${prihodki !== undefined && dobicek !== undefined ? ' oba' : ''}`}>
          {prihodki !== undefined && (
            <GrafPrihodkov ploscica={prihodki} leto={pregled.leto} primerjalno={primerjalno} naCelico={naCelico} mere={STOLPCI_SIROKO} />
          )}
          {dobicek !== undefined && <GrafDobicka ploscica={dobicek} leto={pregled.leto} primerjalno={primerjalno} naCelico={naCelico} />}
        </div>
      )}

      {rezultat !== undefined && (
        <TabelaPrimerjave tabele={[brez(rezultat, ['prihodki-primerjalno'])]} naCelico={naCelico} naslovVrstic={b.kazalnik} vKartici />
      )}

      {(kartaDenarja !== null || kartaRazlike !== null) && (
        <div className={`kz-vrsta-3-2${kartaDenarja !== null && kartaRazlike !== null ? ' oba' : ''}`}>
          {kartaDenarja}
          {kartaRazlike}
        </div>
      )}

      {doMeseca !== undefined && (
        <TabelaPrimerjave
          tabele={[{ ...doMeseca, naslov: b.pk.primerjavaDo(pregled.n), pojasnilo: b.pk.primerjavaPodnaslov(pregled.leto, primerjalno) }]}
          naCelico={naCelico}
          naslovVrstic={b.kazalnik}
          vKartici
          skupineKotVrstice
          puscice
        />
      )}

      {ostale.length > 0 && <TabelaPrimerjave tabele={ostale} naCelico={naCelico} naslovVrstic={b.kazalnik} vKartici />}

      {pregled.n > 0 && <p className="kz-noga-opomba">{b.pk.podrobnoNoga(`${String(dan)}. ${String(pregled.n)}. ${String(pregled.leto)}`)}</p>}
    </div>
  );
}
